// Grüne Erfolgsmeldungen oben in der Mitte des Bildschirms.
//
// Vorher stand „Gespeichert" als Kasten irgendwo auf der Seite — oft weit weg
// von dem Knopf, den man gerade gedrückt hatte, oder ganz oben, während man
// unten in den Einstellungen war. Jetzt erscheint jede Bestätigung an EINER
// Stelle, die man immer sieht, und verschwindet von selbst.
//
// Benutzung:  <Erfolgsmeldung text={meldung} />  statt
//             {meldung && <Hinweis art="erfolg">{meldung}</Hinweis>}
// oder direkt meldungZeigen('…') aus einem Ereignis heraus.
// Fehler bleiben bewusst an Ort und Stelle — die muss man lesen können, während
// man die Eingabe korrigiert.
import { useEffect, useState } from 'react';
import Icon from './Icon';

type Meldung = { id: number; text: string };

let naechste = 1;
const liste: Meldung[] = [];
const hoerer = new Set<() => void>();
const melden = () => hoerer.forEach(f => f());
const SPEICHER = 'lp_meldung_nach_neuladen';
const DAUER_MS = 4500;

/** Zeigt eine grüne Meldung. `nachNeuladen`: auch dann, wenn die Seite gleich neu lädt. */
export function meldungZeigen(text: string, { nachNeuladen = false } = {}) {
  if (!text) return;
  if (nachNeuladen) { try { sessionStorage.setItem(SPEICHER, text); } catch { /* egal */ } }
  // Dieselbe Meldung nicht doppelt übereinander
  if (liste.some(m => m.text === text)) return;
  const m = { id: naechste++, text };
  liste.push(m);
  if (liste.length > 3) liste.shift();
  melden();
  setTimeout(() => entfernen(m.id), DAUER_MS);
}

function entfernen(id: number) {
  const i = liste.findIndex(m => m.id === id);
  if (i >= 0) { liste.splice(i, 1); melden(); }
}

/** Ersatz für {text && <Hinweis art="erfolg">{text}</Hinweis>} — zeigt nichts an Ort und Stelle. */
export function Erfolgsmeldung({ text }: { text: string | null | undefined }) {
  useEffect(() => { if (text) meldungZeigen(text); }, [text]);
  return null;
}

/** Einmal in der App eingehängt — die Leiste selbst. */
export function Meldungsleiste() {
  const [, setStand] = useState(0);
  useEffect(() => {
    const f = () => setStand(n => n + 1);
    hoerer.add(f);
    // Eine Meldung, die vor einem Neuladen ausgelöst wurde, jetzt zeigen.
    try {
      const offen = sessionStorage.getItem(SPEICHER);
      if (offen) { sessionStorage.removeItem(SPEICHER); meldungZeigen(offen); }
    } catch { /* egal */ }
    return () => { hoerer.delete(f); };
  }, []);

  if (!liste.length) return null;
  return (
    <div aria-live="polite" role="status"
         className="pointer-events-none fixed inset-x-0 z-[70] flex flex-col items-center gap-2 px-4"
         style={{ top: 'calc(var(--sicher-oben, 0px) + 0.75rem)' }}>
      {liste.map(m => (
        <button key={m.id} type="button" onClick={() => entfernen(m.id)}
                className="lp-meldung pointer-events-auto flex max-w-lg items-start gap-2.5 rounded-xl bg-emerald-600 px-4 py-2.5 text-start text-sm font-medium text-white shadow-lg shadow-emerald-900/20 ring-1 ring-emerald-500/50">
          <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white/20">
            <Icon name="haken" groesse={14} />
          </span>
          <span>{m.text}</span>
        </button>
      ))}
    </div>
  );
}
